import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Database, ArrowRight, ExternalLink, Satellite, LogOut } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const EASE = [0.16, 1, 0.3, 1];
const EARTHTECH_URL = "https://www.earthtech-ai.com";

const DB_DESC =
  "Earthtech-AI's environmental intelligence platform, combining Indian missions (e.g., Resourcesat, Oceansat, INSAT, EOS) with global open datasets (Sentinel, Landsat, MODIS, VIIRS). providing complementary strengths in spatial resolution, revisit frequency, radar capability, and ocean observations, enabling more robust AI models for agriculture, disaster response, and ecosystem monitoring.";

const MISSIONS = ["Resourcesat", "Oceansat", "INSAT", "EOS", "Sentinel", "Landsat", "MODIS", "VIIRS"];

export default function Portal() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const signOut = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-arjuna-ink text-white" data-testid="portal-page">
      <div className="absolute inset-0 grid-lines opacity-20" />
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full opacity-20 blur-[130px]"
        style={{ background: "radial-gradient(circle, #0044FF, transparent 70%)" }}
      />

      {/* top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5 lg:px-10">
        <Logo className="h-7 w-7" withWordmark />
        <button
          onClick={signOut}
          data-testid="portal-logout"
          className="flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-arjuna-teal"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 pb-20 pt-10 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p className="overline">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</p>
          <h1 className="mt-4 max-w-2xl font-heading text-4xl font-medium tracking-tight sm:text-5xl">
            Where would you like to go?
          </h1>
          <p className="mt-4 max-w-xl text-slate-400">
            Choose to explore the environmental intelligence database, or visit the
            Earthtech-AI website.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Database choice */}
          <motion.button
            data-testid="portal-choice-database"
            onClick={() => navigate("/dashboard")}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-arjuna-teal/40 bg-arjuna-surface p-8 text-left transition-transform duration-300 hover:-translate-y-1 hover:border-arjuna-teal/70 glow-teal"
          >
            <div className="flex items-center justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-arjuna-teal/40 bg-arjuna-teal/10">
                <Database className="h-6 w-6 text-arjuna-teal" />
              </div>
              <span className="rounded-full border border-arjuna-teal/40 bg-arjuna-teal/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-arjuna-teal">
                Recommended
              </span>
            </div>
            <h2 className="mt-6 font-heading text-2xl text-white">Environmental Intelligence Database</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-300">{DB_DESC}</p>

            <div className="mt-6 flex flex-wrap gap-2">
              {MISSIONS.map((m) => (
                <span key={m} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 font-mono text-[10px] tracking-wider text-slate-300">
                  {m}
                </span>
              ))}
            </div>

            <span className="mt-8 inline-flex items-center gap-2 font-medium text-arjuna-teal">
              Enter the database
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </motion.button>

          {/* Website choice */}
          <motion.a
            data-testid="portal-choice-website"
            href={EARTHTECH_URL}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-arjuna-surface/40 p-8 text-left transition-transform duration-300 hover:-translate-y-1 hover:border-white/25"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/15 bg-white/5">
              <Satellite className="h-6 w-6 text-white" />
            </div>
            <h2 className="mt-6 font-heading text-2xl text-white">Earthtech-AI Website</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Visit the Earthtech-AI website to learn more about our environmental
              intelligence platform, missions, and the teams building it.
            </p>
            <div className="mt-6 rounded-xl border border-white/10 bg-white/5 px-4 py-3 font-mono text-xs text-slate-400">
              www.earthtech-ai.com
            </div>
            <span className="mt-8 inline-flex items-center gap-2 font-medium text-white">
              Go to Earthtech-AI
              <ExternalLink className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </motion.a>
        </div>
      </main>
    </div>
  );
}
